import React, { useEffect, useState } from "react";

import { useMsal } from "@azure/msal-react";

import {
  Button,
  Dialog,
  DialogContent,
  Slide,
  Grid,
  Divider,
  Typography,
  IconButton,
  Tooltip,
  Switch,
} from "@mui/material";
import { TransitionProps } from "@mui/material/transitions";

import apiCall from "../utils/apiCall";
import { UPDATE_USER_SETTINGS } from "../endpoint";

import { useAppDispatch, useAppSelector } from "../hooks/reduxhook";
import {
  addUserDetails,
  getuser,
  getSemanticCache,
  semanticCacheToggle,
  getMetadataFlag,
  getPromptCompressor,
  promptCompressorToggle,
  toggleWebSearch,
  modifyChatHistory,
  getChatHistory,
  metadataFlagToggle,
} from "../redux/slices/userSlice";
import { getModelDetails } from "../redux/slices/modelSlice";
import TemperatureSlider from "./Slider";
import SettingRow from "./SettingRow";
import Loader from "./Loader";
import { addError } from "../redux/slices/errorSlice";
import { Add, Close, Remove } from "@mui/icons-material";
import { StyledTooltip } from "./StyledTooltip";
const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<any, any>;
  },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

interface ISettingModal {
  open: boolean;
  setOpen: any;
}

const SettingsModal = ({ open, setOpen }: ISettingModal) => {
  const { accounts } = useMsal();
  const accountId = accounts[0]?.username;
  const user = useAppSelector(getuser);
  const [tempvalue, setTempvalue] = useState(user?.temp ?? 1);
  const [chunk_size, setChunk_size] = useState(user?.chunk_size);
  const [loading, setLoading] = useState(false);
  const model = useAppSelector(getModelDetails);
  const semanticCache = useAppSelector(getSemanticCache);
  const [toggle, setToggle] = React.useState(false);
  const historyValue = useAppSelector(getChatHistory);
  const [semanticCacheEnabled, setSemanticCacheEnabled] = useState(
    semanticCache || false,
  );
  const promptCompressor = useAppSelector(getPromptCompressor);

  const [promptCompressorEnabled, setPromptCompressorEnabled] = useState(
    promptCompressor || false,
  );
  const metadataFlag = useAppSelector(getMetadataFlag);

  const [metadataFlagEnabled, setMetadataFlagEnabled] = useState(
    metadataFlag || false,
  );
  useEffect(() => {
    setSemanticCacheEnabled(semanticCache || false);
  }, [semanticCache]);
  useEffect(() => {
    setPromptCompressorEnabled(promptCompressor || false);
  }, [promptCompressor]);
  useEffect(() => {
    setChunk_size(user?.chunk_size);
  }, [user?.chunk_size]);
  useEffect(() => {
    setMetadataFlagEnabled(metadataFlag || false);
  }, [metadataFlag]);
  useEffect(() => {
    setTempvalue(user?.temp ?? 1);
  }, [user?.temp]);

  const dispatch = useAppDispatch();

  const handleClose = () => {
    setOpen(false);
  };
  const updateSettings = () => {
    setLoading(true);
    apiCall("POST", UPDATE_USER_SETTINGS, {
      temp: tempvalue,
      chunk_size: chunk_size,
      model: model?.model,
      user_email: accountId,
    })
      .then((res) => {
        dispatch(addUserDetails(res?.result));
        handleClose();
      })
      .catch((err) => {
        dispatch(addError("Failed to update the settings.Please try again."));
      })
      .finally(() => {
        setLoading(false);
      });
  };
  return (
    <Dialog
      open={open}
      TransitionComponent={Transition}
      keepMounted
      onClose={handleClose}
      aria-describedby="alert-dialog-slide-description"
      fullWidth={true}
      maxWidth={"sm"}
    >
      {" "}
      <IconButton
        aria-label="close"
        onClick={handleClose}
        size="small"
        sx={{
          position: "absolute",
          right: 2,
          top: 2,
        }}
      >
        <Close sx={{ color: "gray" }} fontSize="small" />
      </IconButton>
      <DialogContent sx={{ p: 4 }}>
        {user?.user_name !== "" && (
          <>
            <Divider
              sx={{
                mb: 4,
              }}
            >
              <Typography
                fontWeight="bolder"
                sx={{
                  fontSize: "18px",
                }}
              >
                Update User Settings
              </Typography>
            </Divider>
            <SettingRow
              title={
                <StyledTooltip
                  title={
                    "Use tempreature to control randomness of text.Higher = more creative, lower = more focused."
                  }
                  placement="left"
                  arrow
                >
                  <Typography component="span" sx={{ fontWeight: "bold" }}>
                    Temperature
                  </Typography>
                </StyledTooltip>
              }
            >
              <Grid item flex={1}>
                <TemperatureSlider
                  testid="TemperatureSlider"
                  setValue={setTempvalue}
                  default_value={0}
                  max={1}
                  min={0}
                  step={0.1}
                  value={tempvalue}
                />
              </Grid>
              <Typography
                sx={{
                  ml: 1,
                }}
              >
                {tempvalue}
              </Typography>
            </SettingRow>
            <SettingRow title="No. of Top Ranked Pages">
              <Grid item flex={1}>
                <TemperatureSlider
                  testid="RankedSlider"
                  setValue={setChunk_size}
                  default_value={chunk_size}
                  max={50}
                  min={1}
                  step={1}
                  value={chunk_size}
                />
              </Grid>
              <Typography
                sx={{
                  ml: 1,
                }}
              >
                {chunk_size}
              </Typography>
            </SettingRow>

            {loading && <Loader />}
            <Button
              variant="contained"
              fullWidth
              sx={{ mt: 4 }}
              onClick={updateSettings}
              color="primary"
              disabled={loading}
              data-testid="settings-button"
            >
              Update Settings
            </Button>
            <SettingRow title="Prompt Compressor">
              <Switch
                color="primary"
                checked={promptCompressorEnabled}
                onChange={(e) => {
                  const checked = e.target.checked;

                  setPromptCompressorEnabled(checked);
                  dispatch(promptCompressorToggle(checked));
                }}
              />

              <Typography
                sx={{
                  ml: 1,
                  fontSize: "15px",
                }}
              >
                Enable Prompt Compressor
              </Typography>
            </SettingRow>
            <SettingRow title="Caching">
              {/* <Grid
                item
                sx={{
                  display: "flex",
                  alignItems: "center",
                  width: "100%",
                }}
              > */}
              <Switch
                color="primary"
                checked={semanticCacheEnabled}
                onChange={(e) => {
                  const checked = e.target.checked;

                  setSemanticCacheEnabled(checked);
                  dispatch(semanticCacheToggle(checked));
                }}
              />

              <Typography
                sx={{
                  ml: 1,
                  fontSize: "15px",
                }}
              >
                Enable Caching
              </Typography>
              {/* </Grid> */}
            </SettingRow>
            <SettingRow title="Chat History">
              <Grid
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                }}
              >
                <Switch
                  defaultChecked
                  color="primary"
                  onChange={(e) => {
                    if (e.target.checked) {
                      dispatch(modifyChatHistory(3));
                    } else {
                      dispatch(modifyChatHistory(0));
                    }
                    setToggle(e.target.checked);
                  }}
                  checked={toggle}
                />
                <Typography
                  sx={{
                    borderRight: "1px solid rgba(0,0,0,.1)",
                    pr: 0.5,
                    mr: 0.5,
                    fontSize: "15px",
                    width: "120px",
                  }}
                >
                  {toggle ? "Chat History" : "No Chat History"}
                </Typography>
                {toggle && (
                  <>
                    <Remove
                      fontSize="small"
                      color="primary"
                      onClick={() =>
                        historyValue > 0
                          ? dispatch(modifyChatHistory(historyValue - 1))
                          : ""
                      }
                      sx={{
                        cursor: "pointer",
                        width: "14px",
                        height: "12px",
                        mr: 1,
                      }}
                    />
                    <Typography sx={{ fontSize: "10px" }}>
                      {historyValue}
                    </Typography>
                    <Add
                      fontSize="small"
                      color="primary"
                      onClick={() =>
                        dispatch(modifyChatHistory(historyValue + 1))
                      }
                      sx={{
                        cursor: "pointer",
                        width: "14px",
                        height: "14px",
                        ml: 0.5,
                      }}
                    />
                  </>
                )}
              </Grid>
            </SettingRow>
            <SettingRow title="Metadata">
              <Switch
                color="primary"
                checked={metadataFlagEnabled}
                onChange={(e) => {
                  const checked = e.target.checked;

                  setMetadataFlagEnabled(checked);
                  dispatch(metadataFlagToggle(checked));
                }}
              />

              <Typography
                sx={{
                  ml: 1,
                  fontSize: "15px",
                }}
              >
                Enable Metadata
              </Typography>
            </SettingRow>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default SettingsModal;
